import { useEffect, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import { trackException, trackInfo, LogLevel } from "../services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../services/appInsightsConstants";

export const useSessionSync = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const listenerAddedRef = useRef(false);

  const checkSessionStatus = () => {
    console.log("Checking session status...");

    axiosInstance
      .get(window.config.HORIZON_API_URL + "/token/status", {
        withCredentials: true,
      })
      .then(() => {
        // No logging for successful session checks - only log redirections

        if (
          !window.location.pathname.includes("/product-selection") &&
          !window.location.pathname.includes("/change-password") &&
          !window.location.pathname.includes("/account/forgot-password")
        ) {
          trackInfo('Redirecting to product selection due to valid session', {
            component: AppInsightsComponents.SESSION_SYNC,
            action: AppInsightsActions.NAVIGATION_SUCCESS,
            fromPath: window.location.pathname,
            toPath: '/product-selection'
          });

          window.location.href =
            window.config.SHELL_APP_URL + "/product-selection";
        }
      })
      .catch((error) => {
        // Only log unexpected session errors (not expired tokens)
        if (error.response?.status !== 401) {
          trackException(
            error,
            LogLevel.Warning,
            {
              component: AppInsightsComponents.SESSION_SYNC,
              action: AppInsightsActions.SESSION_CHECK_FAILURE,
              errorType: AppInsightsErrorTypes.AUTH_ERROR,
              currentPath: window.location.pathname,
              statusCode: error.response?.status || 0,
              errorMessage: error.message
            }
          );
        }

        if (
          !window.location.pathname.includes("/account/login") &&
          !window.location.pathname.includes("/account/register") &&
          !window.location.pathname.includes("/account/forgot-password") &&
          !window.location.pathname.includes("/invite")
        ) {
          trackInfo('Redirecting to login due to invalid session', {
            component: AppInsightsComponents.SESSION_SYNC,
            action: AppInsightsActions.NAVIGATION_SUCCESS,
            fromPath: window.location.pathname,
            toPath: '/account/login'
          });

          window.location.href =
            window.config.SHELL_APP_URL + "/account/login";
        }
      });
  };

  // This hook is responsible for syncing the session status across tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Check session via your API (no logging for routine checks)
        checkSessionStatus();
        // Optionally, you can also set an interval to check the session status periodically
        if (intervalRef.current === null) {
          intervalRef.current = setInterval(() => {
            if (document.visibilityState === "visible") {
              checkSessionStatus(); // No logging for periodic checks
            }
          }, 5 * 60 * 1000); // 5 minutes
        }
      } else {
        // Clear interval when tab becomes hidden (no logging needed)

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    if (!listenerAddedRef.current) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      listenerAddedRef.current = true;
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      listenerAddedRef.current = false;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);
};
